import * as React from 'react'
import type { StockVelocityRow } from '@/lib/api-client'

interface Props {
  velocity: StockVelocityRow[]
  demo: boolean
  mask: (skuId: number, name: string) => string
  onSelectSku: (skuId: number) => void
  topN?: number
}

const BRANCH_ORDER = ['Putrajaya', 'Bangi', 'Shah Alam', 'Kuala Lumpur']

/** SKU × branch heatmap. Cell colour = qty relative to that SKU's busiest branch. */
export function VelocityHeatmap({ velocity, demo, mask, onSelectSku, topN = 15 }: Props) {
  const { skus, branches, cell, rowTotal, rowMax } = React.useMemo(() => {
    const skuMap = new Map<number, string>()
    const branchSet = new Set<string>()
    const cell = new Map<string, number>() // `${skuId}|${branch}` -> qty
    const rowTotal = new Map<number, number>()
    const rowMax = new Map<number, number>()
    for (const v of velocity) {
      skuMap.set(v.skuId, v.sku)
      branchSet.add(v.branch)
      cell.set(`${v.skuId}|${v.branch}`, v.qty)
      rowTotal.set(v.skuId, (rowTotal.get(v.skuId) ?? 0) + v.qty)
      rowMax.set(v.skuId, Math.max(rowMax.get(v.skuId) ?? 0, v.qty))
    }
    const branches = BRANCH_ORDER.filter((b) => branchSet.has(b)).concat(
      [...branchSet].filter((b) => !BRANCH_ORDER.includes(b)),
    )
    const skus = [...skuMap.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => (rowTotal.get(b.id) ?? 0) - (rowTotal.get(a.id) ?? 0))
      .slice(0, topN)
    return { skus, branches, cell, rowTotal, rowMax }
  }, [velocity, topN])

  if (!skus.length) {
    return <div className="rounded-xl border border-border bg-card p-8 text-center text-text-muted text-sm">No stock movement in this window.</div>
  }

  return (
    <div className="rounded-xl border border-border bg-card p-4 overflow-x-auto">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-text-dark">Velocity heatmap — units sold per branch</h3>
        <span className="text-[11px] text-text-muted">darker = higher demand · click a row for trend</span>
      </div>
      <table className="w-full border-separate" style={{ borderSpacing: '3px' }}>
        <thead>
          <tr>
            <th className="text-left text-[11px] font-medium text-text-muted px-2 w-[200px]">SKU</th>
            {branches.map((b) => (
              <th key={b} className="text-center text-[11px] font-medium text-text-muted px-1 min-w-[72px]">{b}</th>
            ))}
            <th className="text-center text-[11px] font-medium text-text-muted px-1 min-w-[60px]">Total</th>
          </tr>
        </thead>
        <tbody>
          {skus.map((sku) => {
            const max = rowMax.get(sku.id) || 1
            return (
              <tr
                key={sku.id}
                className="cursor-pointer group"
                onClick={() => onSelectSku(sku.id)}
              >
                <td className="text-[12px] text-text-dark px-2 py-1 truncate max-w-[200px] group-hover:text-info">
                  {mask(sku.id, sku.name)}
                </td>
                {branches.map((b) => {
                  const qty = cell.get(`${sku.id}|${b}`) ?? 0
                  const intensity = qty / max // 0..1
                  const bg =
                    qty === 0
                      ? 'transparent'
                      : `rgba(37, 99, 235, ${0.12 + intensity * 0.78})` // blue ramp
                  const textColor = intensity > 0.55 ? '#fff' : 'var(--color-text-dark, #0f172a)'
                  return (
                    <td
                      key={b}
                      className="text-center text-[12px] rounded-md py-1.5 font-medium"
                      style={{ background: bg, color: qty === 0 ? 'var(--color-text-muted,#94a3b8)' : textColor }}
                    >
                      {qty || '·'}
                    </td>
                  )
                })}
                <td className="text-center text-[12px] font-semibold text-text-dark py-1.5">
                  {rowTotal.get(sku.id)}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
